import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useController, useForm } from 'react-hook-form'

import { DOMAIN_MIN_LENGTH } from '../constants'
import { getAppDomain } from 'config'

import { Form, Input, Main } from '../../../components'

import {
  setOrganizationDetails,
  getCreationState,
  getOrganization,
  checkDomainAvailability,
  setStep,
} from './state'
import { CreateSteps } from './CreateSteps'
import { FormattedMessage, useIntl } from 'react-intl'

type FormData = {
  domain: string
}

export function TeamUrlForm({ superEnabled }: { superEnabled: boolean }) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const creationState = useSelector(getCreationState)
  const appDomain = `.${useSelector(getAppDomain)}`
  const organization = useSelector(getOrganization)
  const { isCheckingDomain, domainCheckError, domainIsAvailable } =
    creationState

  const { formState, watch, handleSubmit, control, trigger } =
    useForm<FormData>({
      mode: 'onChange',
    })
  const domain = watch('domain')

  useEffect(() => {
    dispatch(checkDomainAvailability(domain))
  }, [dispatch, domain])

  const onSubmit = (data: FormData) => {
    dispatch(setOrganizationDetails({ domain: data.domain }, false))
    dispatch(setStep(CreateSteps.Creating))
  }

  const rules = {
    required: intl.formatMessage(
      {
        id: 'boarding.organization.create.teamUrl.required',
        defaultMessage: 'The URL must be at least {lenght} characters long.',
      },
      {
        lenght: DOMAIN_MIN_LENGTH,
      }
    ),
    minLength: {
      value: DOMAIN_MIN_LENGTH,
      message: intl.formatMessage(
        {
          id: 'boarding.organization.create.teamUrl.minLength',
          defaultMessage: 'The URL must be at least {lenght} characters long.',
        },
        {
          lenght: DOMAIN_MIN_LENGTH,
        }
      ),
    },
    pattern: {
      value: /^[a-z0-9][a-z0-9-]{2,}[a-z0-9]$/,
      message: intl.formatMessage({
        id: 'boarding.organization.create.teamUrl.pattern',
        defaultMessage: 'Invalid domain name.',
      }),
    },
    validate: {
      available: () => {
        if (domainCheckError) {
          return typeof domainCheckError.message === 'string'
            ? domainCheckError.message
            : intl.formatMessage({
                id: 'boarding.organization.create.teamUrl.error',
                defaultMessage: 'Error checking domain availability',
              })
        } else if (!isCheckingDomain && domainIsAvailable !== true) {
          return intl.formatMessage({
            id: 'boarding.organization.create.teamUrl.notAvailable',
            defaultMessage: 'This URL is not available. Sorry! Try another.',
          })
        }
      },
    },
  }

  useEffect(() => {
    trigger('domain')
  }, [trigger, isCheckingDomain, domainIsAvailable, domainCheckError])

  const {
    field: { onChange, ...field },
    fieldState,
  } = useController({
    name: 'domain',
    control,
    rules,
    defaultValue: organization.domain || '',
  })

  return (
    <Main.Container>
      <form
        className="flexbox flexbox-column form-grid"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Main.Title>
          <FormattedMessage
            id="boarding.createOrg.chooseAddress"
            defaultMessage="Last step, choose an address"
          />
        </Main.Title>
        <div>
          <Form.Label>
            <FormattedMessage
              id="boarding.createOrg.domain"
              defaultMessage="Your workspace URL"
              values={{}}
            />
          </Form.Label>
          <Input
            className="margin-vertical-8"
            renderRight={({ className }) => (
              <div className={className}>{appDomain}</div>
            )}
            autoComplete="off"
            autoFocus
            placeholder={intl.formatMessage({
              id: 'boarding.createOrg.domain.placeholder',
              defaultMessage: 'your-team-address',
            })}
            data-test-id="orgDomainField"
            error={fieldState.invalid}
            onChange={({ target: { value } }) => onChange(value.toLowerCase())}
            {...field}
          />
          <div className="font-size-small shade-20 margin-bottom-8">
            <FormattedMessage
              id="boarding.createOrg.domain.description"
              defaultMessage="You'll use it to sign in to {product}. Letters, numbers, and hyphens
            only."
              values={{ product: superEnabled ? 'Super' : 'Slite' }}
            />
          </div>
          <Form.Error message={formState.errors?.domain?.message} />
        </div>
        <Form.Button disabled={!formState.isValid || isCheckingDomain}>
          <FormattedMessage
            id="boarding.createOrg.openSlite"
            defaultMessage="Open your {product}"
            values={{ product: superEnabled ? 'Super' : 'Slite' }}
          />
        </Form.Button>
      </form>
    </Main.Container>
  )
}
